<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      group-by="code"
      show-expand
      item-key="key"
      single-expand
      :expanded.sync="expanded"
      class="safety-elevation-3 table-striped"
      :loading="loading"
      :no-data-text="noDataText"
      :footer-props="{
        'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
        'items-per-page-all-text': `${$t('employees.footer.all')}`,
      }"
    >
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" style="padding-top: 16px;">
          <div v-html="item.content.content"></div>
        </td>
      </template>

      <template v-slot:[`item.menteeText`]="{ item }">
        <router-link :to="{ name: 'employee', params: { id: item.mentee.id } }"
          >{{ item.menteeText }}
        </router-link>
      </template>

      <template v-slot:[`item.version`]="{ item }">
        <span>{{ item.version.revision }} ({{ item.version | date }})</span>
      </template>

      <template v-slot:[`item.content`]="{ item }">
        <span class="font-weight-bold">{{ item.content.title }} </span>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <v-btn
          v-if="
            (item.status == 'assign' || item.status == 'approve') &&
              flow[item.next].allowed(item)
          "
          small
          color="primary"
          @click="sign(item, item.next)"
        >
          {{ $t("profile.mentorship.actions." + item.next + ".create.action") }}
        </v-btn>
        <v-chip
          v-else
          small
          :color="
            item.status && item.status == 'confirm'
              ? 'green lighten-4'
              : 'orange lighten-4'
          "
          :class="
            item.status && item.status == 'confirm'
              ? 'success--text text--darken-1 text-uppercase mr-1'
              : 'warning--text text--darken-1 text-uppercase mr-1'
          "
        >
          {{
            item.status
              ? $t("profile.mentorship.actions." + item.status + ".status")
              : $t("profile.mentorship.actions." + item.next + ".action")
          }}
        </v-chip>
      </template>
      <template v-slot:[`item.menu`]="{ item }">
        <v-menu
          bottom
          left
          v-if="item.signature && flow[item.signature.type].revokeAllowed(item)"
        >
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <!-- revoke last step -->
            <v-list-item @click="revoke(item.signature)">
              <v-icon small left>close</v-icon>
              {{
                $t(
                  `profile.mentorship.actions.${item.signature.type}.revoke.title`
                )
              }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <impersonation
      v-if="impersonateDialog.status"
      :show="impersonateDialog.show"
      :title="
        $t(
          `profile.mentorship.actions.${impersonateDialog.status}.create.title`
        )
      "
      :text="
        $t(`profile.mentorship.actions.${impersonateDialog.status}.create.text`)
      "
      :action="
        $t(
          `profile.mentorship.actions.${impersonateDialog.status}.create.action`
        )
      "
      @confirm="impersonateDialog.callback"
      @cancel="impersonateDialog.show = false"
    />

    <revoke-modal
      v-if="revokeDialog.status"
      :show="revokeDialog.show"
      :title="
        $t(`profile.mentorship.actions.${revokeDialog.status}.revoke.title`)
      "
      :text="
        $t(`profile.mentorship.actions.${revokeDialog.status}.revoke.text`)
      "
      :action="
        $t(`profile.mentorship.actions.${revokeDialog.status}.revoke.action`)
      "
      @confirm="revokeDialog.callback"
      @cancel="revokeDialog.show = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import Impersonation from "@/components/Impersonation";
import RevokeModal from "@/components/RevokeModal";

import ApproveMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/ApproveMentorshipAction.gql";
import ConfirmMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/ConfirmMentorshipAction.gql";
import RevokeMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/RevokeMentorshipAction.gql";

import {
  getTagText,
  getMentor,
  getSignatureState,
} from "@/utils/mentorshipActions.js";

export default {
  props: {
    activeMentorshipActions: {
      type: Array,
      required: true,
    },
    id: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
    },
    noDataText: {
      type: String,
    },
  },

  components: {
    Impersonation,
    RevokeModal,
  },

  data() {
    return {
      expanded: [],

      impersonateDialog: {
        show: false,
        status: null,
        callback: () => {},
      },

      revokeDialog: {
        show: false,
        status: null,
        callback: () => {},
      },

      flow: {
        assign: {
          allowed: (item) =>
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (this.me && item.mentorship.mentor.id == this.me.id),
          revokeAllowed: (item) =>
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (this.me && item.mentorship.mentor.id == this.me.id) ||
            (this.me && item.mentor && item.mentor.id == this.me.id),
        },
        approve: {
          allowed: (item) => this.me && this.me.id == item.mentor.id,
          revokeAllowed: (item) =>
            (this.me && this.me.id == item.mentor.id) ||
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (this.me && this.me.id == item.mentorship.mentor.id) ||
            (this.me && this.me.id == item.mentee.id),
          query: ApproveMentorshipActionQuery,
        },

        confirm: {
          allowed: (item) =>
            (this.me && this.me.id == item.mentee.id) ||
            this.id == item.mentee.id,
          revokeAllowed: (item) => this.me && this.me.id == item.mentee.id,
          query: ConfirmMentorshipActionQuery,
        },
      },
    };
  },

  computed: {
    ...mapState(["me"]),
    ...mapGetters(["role"]),

    headers() {
      const defaultClass = "text-uppercase subheading text-truncate";
      const h = [
        {
          text: this.$i18n.t("mentorship.actions.column.tag"),
          value: "tagText",
          class: defaultClass,
        },
        {
          text: this.$i18n.t("mentorship.actions.column.mentee"),
          value: "menteeText",
          class: defaultClass,
        },
        {
          text: this.$i18n.t("mentorship.actions.column.code"),
          value: "code",
          class: defaultClass,
        },
        {
          text: this.$i18n.t("mentorship.actions.column.name"),
          value: "name",
          class: defaultClass,
        },
        {
          text:
            this.$i18n.t("mentorship.actions.column.revision") +
            " / " +
            this.$i18n.t("mentorship.actions.column.date"),
          value: "version",
          class: defaultClass,
        },

        {
          text: this.$i18n.t("mentorship.actions.column.content"),
          value: "content",
          class: defaultClass,
        },
        {
          text: this.$i18n.t("mentorship.actions.column.action"),
          value: "action",
          class: defaultClass,
        },
        {
          text: "",
          value: "menu",
          class: defaultClass,
        },
      ];
      return h;
    },

    items() {
      const items = [];
      for (const action of this.activeMentorshipActions) {
        for (const version of action.assignedVersions) {
          for (const content of version.content) {
            const signatureState = getSignatureState(content.signatures, [
              "confirm",
              "approve",
              "assign",
            ]);
            const mentor = getMentor(content.signatures);
            const item = {
              key: `${content.assignedId}_${content.id}`,
              tagText: getTagText(version.mentorship.tag),
              code: action.code,
              name: version.name.name,
              mentee: version.mentorship.employee,
              menteeText: this.$options.filters.nameId(
                version.mentorship.employee
              ),
              mentor,
              mentorship: version.mentorship,
              version: version,
              content: content.contentLocale,
              ...signatureState,
              assignedVersionId: content.assignedId,
            };
            if (mentor && this.id == mentor.id) {
              items.push(item);
            }
          }
        }
      }
      return items;
    },
  },
  methods: {
    sign(item, status, args) {
      if (!args && !this.flow[status].noPassword) {
        this.impersonateDialog = {
          callback: (args) => this.sign(item, status, args),
          status,
          show: true,
        };
      } else {
        this.impersonateDialog = {
          callback: () => {},
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: this.flow[status].query,
            variables: {
              password: args && args.password,
              employeeId: this.me.id,
              mentorId: item.mentor.id,
              assignedVersionId: item.assignedVersionId,
              contentId: item.content.contentId,
              localeContentId: item.content.id,
            },
          })
          .then(() => {})
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          })
          .finally(() => {
            this.$emit("refresh");
          });
      }
    },
    revoke(signature, args) {
      if (!args) {
        this.revokeDialog = {
          callback: (args) => this.revoke(signature, args),
          status: signature.type,
          show: true,
        };
      } else {
        this.revokeDialog = {
          callback: () => {},
          status: null,
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: RevokeMentorshipActionQuery,
            variables: {
              signatureId: signature.id,
              reason: args.reason,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },
  },
};
</script>
